<template>
  <div 
    class="modal fade" 
    :class="{ show: visible }" 
    :style="{ display: visible ? 'block' : 'none' }"
    tabindex="-1" 
    aria-hidden="true"
    @click.self="cerrarModal"
  >
    <div class="modal-dialog modal-xl">
      <div :class="themeClasses.modalContent" class="modal-content">
        <div :class="themeClasses.modalHeader" class="modal-header">
          <h5 :class="themeClasses.modalTitle" class="modal-title">
            <i class="fas fa-key me-2"></i>
            Gestionar Permisos - {{ usuario?.full_name }}
          </h5>
          <button type="button" :class="themeClasses.btnClose" class="btn-close" @click="cerrarModal"></button>
        </div>
        
        <div :class="themeClasses.modalBody" class="modal-body">
          <!-- Loading -->
          <div v-if="loading" class="text-center py-5">
            <div :class="themeClasses.spinner" class="spinner-border" role="status">
              <span class="visually-hidden">Cargando...</span>
            </div>
            <p :class="themeClasses.mutedText" class="mt-3">Cargando permisos...</p>
          </div>

          <!-- Contenido -->
          <div v-else>
            <!-- Información del Usuario -->
            <div class="alert alert-info d-flex align-items-center mb-4">
              <i class="fas fa-info-circle me-3"></i>
              <div>
                <strong>Usuario:</strong> {{ usuario.full_name }} ({{ usuario.username }}) <br>
                <strong>Rol:</strong> 
                <span class="badge" :class="badgeRol(usuario.role)">
                  {{ formatearRol(usuario.role) }}
                </span>
              </div>
            </div>

            <!-- Acciones Rápidas -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card" :class="themeClass" style="background-color: var(--secondary-bg-muted);">
                  <div class="card-body">
                    <h6 class="card-title">
                      <i class="fas fa-magic text-primary me-2"></i>
                      Acciones Rápidas
                    </h6>
                    <div class="btn-group w-100" role="group">
                      <button 
                        class="btn btn-outline-success btn-sm"
                        @click="aplicarPermisosTodos(true)"
                        :disabled="guardando"
                      >
                        <i class="fas fa-check-double me-1"></i>
                        Marcar Todos
                      </button>
                      <button 
                        class="btn btn-outline-warning btn-sm"
                        @click="aplicarPermisosTodos(false)"
                        :disabled="guardando"
                      >
                        <i class="fas fa-times me-1"></i>
                        Desmarcar Todos
                      </button>
                      <button 
                        class="btn btn-outline-info btn-sm"
                        @click="aplicarPermisosRol"
                        :disabled="guardando"
                      >
                        <i class="fas fa-user-tag me-1"></i>
                        Por Rol
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card" :class="themeClass" style="background-color: var(--secondary-bg-muted);">
                  <div class="card-body">
                    <h6 class="card-title">
                      <i class="fas fa-chart-pie text-info me-2"></i>
                      Resumen de Permisos
                    </h6>
                    <div class="row text-center">
                      <div class="col-4">
                        <div class="h5 mb-0 text-success">{{ permisosActivos }}</div>
                        <small class="text-muted">Activos</small>
                      </div>
                      <div class="col-4">
                        <div class="h5 mb-0 text-danger">{{ permisosInactivos }}</div>
                        <small class="text-muted">Inactivos</small>
                      </div>
                      <div class="col-4">
                        <div class="h5 mb-0 text-primary">{{ totalPermisos }}</div>
                        <small class="text-muted">Total</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Permisos por Módulo -->
            <div class="accordion" id="accordionPermisos">
              <div 
                v-for="(modulo, moduloNombre) in permisosAgrupados" 
                :key="moduloNombre"
                class="accordion-item"
              >
                <h2 class="accordion-header">
                  <button 
                    class="accordion-button"
                    :class="{ collapsed: !modulo.expandido }"
                    type="button" 
                    @click="toggleModulo(moduloNombre)"
                  >
                    <i :class="iconoModulo(moduloNombre)" class="me-2"></i>
                    <strong>{{ formatearModulo(moduloNombre) }}</strong>
                    <span class="badge bg-primary ms-auto me-3">
                      {{ modulo.activos }} / {{ modulo.permisos.length }}
                    </span>
                  </button>
                </h2>
                <div 
                  class="accordion-collapse collapse"
                  :class="{ show: modulo.expandido }"
                >
                  <div class="accordion-body">
                    <!-- Controles del Módulo -->
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <div class="form-check">
                        <input 
                          class="form-check-input" 
                          type="checkbox"
                          :id="`modulo-${moduloNombre}`"
                          :checked="modulo.activos === modulo.permisos.length"
                          :indeterminate.prop="modulo.activos > 0 && modulo.activos < modulo.permisos.length"
                          @change="toggleModuloCompleto(moduloNombre, $event.target.checked)"
                        >
                        <label 
                          class="form-check-label fw-bold" 
                          :for="`modulo-${moduloNombre}`"
                        >
                          Habilitar todo el módulo
                        </label>
                      </div>
                      <small class="text-muted">
                        {{ modulo.activos }} de {{ modulo.permisos.length }} permisos activos
                      </small>
                    </div>

                    <!-- Lista de Permisos -->
                    <div class="row g-2">
                      <div 
                        v-for="permiso in modulo.permisos" 
                        :key="permiso.permission_name"
                        class="col-md-6 col-lg-4"
                      >
                        <div class="card border-0" :class="themeClass" style="background-color: var(--secondary-bg-muted);">
                          <div class="card-body p-3">
                            <div class="form-check">
                              <input 
                                class="form-check-input" 
                                type="checkbox"
                                :id="permiso.permission_name"
                                v-model="permisosUsuario[permiso.permission_name]"
                                @change="permisoChanged"
                              >
                              <label 
                                class="form-check-label d-block" 
                                :for="permiso.permission_name"
                              >
                                <div class="fw-bold">{{ formatearPermiso(permiso.action) }}</div>
                                <small class="text-muted">{{ permiso.description }}</small>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Alerta de cambios -->
            <div v-if="hayCambios" class="alert alert-warning d-flex align-items-center mt-4">
              <i class="fas fa-exclamation-triangle me-3"></i>
              <div class="flex-grow-1">
                <strong>Tienes cambios sin guardar.</strong>
                Los cambios se aplicarán cuando presiones "Guardar Permisos".
              </div>
              <button 
                class="btn btn-outline-warning btn-sm ms-3"
                @click="resetearPermisos"
              >
                Descartar
              </button>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cerrarModal">
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            @click="guardarPermisos"
            :disabled="loading || guardando || !hayCambios"
          >
            <i v-if="guardando" class="fas fa-spinner fa-spin me-2"></i>
            <i v-else class="fas fa-save me-2"></i>
            Guardar Permisos
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Backdrop -->
  <div v-if="visible" class="modal-backdrop fade show"></div>
</template>

<script>
import PermisosService from '@/services/permisos'
import { Toast } from '@/utils/notifications'

import themeMixin from '@/mixins/themeMixin'

export default {
  mixins: [themeMixin],
  name: 'AdminConfiguracionPermisos',
  props: {
    usuario: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'saved'],
  data() {
    return {
      loading: true,
      guardando: false,
      
      permisosDisponibles: [],
      permisosUsuario: {},
      permisosOriginales: {},
      
      permisosAgrupados: {}
    }
  },
  computed: {
    totalPermisos() {
      return this.permisosDisponibles.length
    },
    
    permisosActivos() {
      return Object.values(this.permisosUsuario).filter(Boolean).length
    },
    
    permisosInactivos() {
      return this.totalPermisos - this.permisosActivos
    },
    
    hayCambios() {
      return JSON.stringify(this.permisosUsuario) !== JSON.stringify(this.permisosOriginales)
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.cargarPermisos()
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  methods: {
    async cargarPermisos() {
      try {
        this.loading = true
        
        // Cargar permisos disponibles
        const responseDisponibles = await PermisosService.getPermisosDisponibles()
        
        if (!responseDisponibles.success) {
          Toast.error('Error al cargar permisos disponibles')
          return
        }
        
        this.permisosDisponibles = responseDisponibles.data.permisos || []
        
        // Cargar permisos del usuario
        const responseUsuario = await PermisosService.getPermisosUsuario(this.usuario.id)
        
        if (!responseUsuario.success) {
          Toast.error('Error al cargar permisos del usuario')
          return
        }
        
        // Inicializar permisos del usuario
        this.permisosUsuario = {}
        
        this.permisosDisponibles.forEach(permiso => {
          this.permisosUsuario[permiso.permission_name] = false
        })
        
        // Marcar permisos activos
        if (responseUsuario.data.permisos) {
          responseUsuario.data.permisos.forEach(permiso => {
            this.permisosUsuario[permiso.permission_name] = true
          })
        }
        
        // Guardar estado original
        this.permisosOriginales = { ...this.permisosUsuario }
        
        // Agrupar permisos por módulo
        this.agruparPermisos()
        
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al cargar permisos')
      } finally {
        this.loading = false
      }
    },

    agruparPermisos() {
      this.permisosAgrupados = {}
      
      this.permisosDisponibles.forEach(permiso => {
        const modulo = permiso.module
        
        if (!this.permisosAgrupados[modulo]) {
          this.permisosAgrupados[modulo] = {
            permisos: [],
            activos: 0,
            expandido: modulo === 'usuarios' // Expandir módulo de usuarios por defecto
          }
        }
        
        this.permisosAgrupados[modulo].permisos.push(permiso)
        
        if (this.permisosUsuario[permiso.permission_name]) {
          this.permisosAgrupados[modulo].activos++
        }
      })
    },

    permisoChanged() {
      this.$nextTick(() => {
        this.agruparPermisos()
      })
    },

    toggleModulo(moduloNombre) {
      this.permisosAgrupados[moduloNombre].expandido = !this.permisosAgrupados[moduloNombre].expandido
    },

    toggleModuloCompleto(moduloNombre, activar) {
      const modulo = this.permisosAgrupados[moduloNombre]
      
      modulo.permisos.forEach(permiso => {
        this.permisosUsuario[permiso.permission_name] = activar
      })
      
      this.permisoChanged()
    },

    aplicarPermisosTodos(activar) {
      Object.keys(this.permisosUsuario).forEach(permiso => {
        this.permisosUsuario[permiso] = activar
      })
      
      this.permisoChanged()
    },

    aplicarPermisosRol() {
      // Aplicar permisos según el rol del usuario
      const permisosRol = this.getPermisosPorRol(this.usuario.role)
      
      Object.keys(this.permisosUsuario).forEach(permiso => {
        this.permisosUsuario[permiso] = permisosRol.includes(permiso)
      })
      
      this.permisoChanged()
      Toast.info(`Permisos aplicados según el rol: ${this.formatearRol(this.usuario.role)}`)
    },

    getPermisosPorRol(rol) {
      const permisosPorRol = {
        admin: this.permisosDisponibles.map(p => p.permission_name), // Todos los permisos
        manager: [
          'clientes_view', 'clientes_create', 'clientes_edit',
          'productos_view', 'productos_create', 'productos_edit',
          'facturacion_view', 'facturacion_create', 'facturacion_edit',
          'reportes_view', 'reportes_export',
          'empresas_view', 'empresas_edit'
        ],
        user: [
          'clientes_view', 'clientes_create',
          'productos_view',
          'facturacion_view', 'facturacion_create'
        ]
      }
      
      return permisosPorRol[rol] || []
    },

    async guardarPermisos() {
      try {
        this.guardando = true
        
        // Preparar datos para envío
        const permisosActivos = Object.keys(this.permisosUsuario)
          .filter(permiso => this.permisosUsuario[permiso])
        
        const response = await PermisosService.updatePermisosUsuario(
          this.usuario.id, 
          permisosActivos
        )
        
        if (response.success) {
          this.permisosOriginales = { ...this.permisosUsuario }
          this.$emit('saved')
          this.cerrarModal()
        } else {
          Toast.error(response.error || 'Error al guardar permisos')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al guardar permisos')
      } finally {
        this.guardando = false
      }
    },

    resetearPermisos() {
      this.permisosUsuario = { ...this.permisosOriginales }
      this.agruparPermisos()
      Toast.info('Cambios descartados')
    },

    cerrarModal() {
      document.body.style.overflow = ''
      this.$emit('close')
    },

    // Métodos de formateo
    badgeRol(rol) {
      const badges = {
        admin: 'bg-danger',
        manager: 'bg-warning',
        user: 'bg-info'
      }
      return badges[rol] || 'bg-secondary'
    },

    formatearRol(rol) {
      const roles = {
        admin: 'Administrador',
        manager: 'Gerente',
        user: 'Usuario'
      }
      return roles[rol] || rol
    },

    formatearModulo(modulo) {
      const modulos = {
        usuarios: 'Gestión de Usuarios',
        clientes: 'Gestión de Clientes',
        productos: 'Gestión de Productos',
        facturacion: 'Facturación',
        reportes: 'Reportes',
        empresas: 'Gestión de Empresas'
      }
      return modulos[modulo] || modulo
    },

    formatearPermiso(accion) {
      const acciones = {
        view: 'Ver',
        create: 'Crear',
        edit: 'Editar',
        delete: 'Eliminar',
        export: 'Exportar',
        import: 'Importar'
      }
      return acciones[accion] || accion
    },

    iconoModulo(modulo) {
      const iconos = {
        usuarios: 'fas fa-users',
        clientes: 'fas fa-user-friends',
        productos: 'fas fa-box',
        facturacion: 'fas fa-file-invoice',
        reportes: 'fas fa-chart-bar',
        empresas: 'fas fa-building'
      }
      return iconos[modulo] || 'fas fa-cog'
    }
  }
}
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-xl {
  max-width: 1200px;
}

.accordion-button {
  padding: 1rem 1.25rem;
}

.accordion-button:not(.collapsed) {
  color: #0c63e4;
  background-color: #e7f1ff;
}

.form-check-input:indeterminate {
  background-color: #fd7e14;
  border-color: #fd7e14;
}

.card {
  transition: all 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-label {
  cursor: pointer;
}

.badge {
  font-size: 0.75rem;
}

.alert {
  border-radius: 0.375rem;
}

.btn-group .btn {
  border-radius: 0;
}

.btn-group .btn:first-child {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.btn-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
    margin: 1rem auto;
  }
  
  .col-md-6,
  .col-lg-4 {
    margin-bottom: 0.5rem;
  }
}
</style>
